/**
 * El borrador de alta que sobrevive a cerrar la pestaña (US5 · FR-022…FR-028).
 *
 * POR QUÉ ESTO NO VIVE DENTRO DE `nueva.tsx`
 * ─────────────────────────────────────────
 * Porque el formulario ya ocupa cuatrocientas líneas y esto no es formulario:
 * es un ciclo de vida —abrir, guardar, chocar con otra persona— con reglas
 * propias que se explican solas. Mezclado con los campos, esas reglas se leen
 * como detalles del `onChange` de turno.
 *
 * LAS DOS REGLAS QUE MANDAN AQUÍ
 * ──────────────────────────────
 *
 * 1. **Guardar no valida** (FR-022). Un guardado que rechaza pierde justo el
 *    trabajo que venía a salvar. Lo que falte se dice al abrirlo y al emitirlo.
 *
 * 2. **El choque se cuenta con nombre y hora** (FR-028). Un «conflicto» a secas
 *    deja a quien lo lee sin saber si ha perdido lo suyo; y lo que hay en
 *    pantalla **no se toca**, porque tirarlo sería cometer el daño que el
 *    candado existe para evitar.
 */
import { useEffect, useState } from 'react'

import { ErrorApi } from '../../lib/api'
import {
    crearBorradorApi,
    guardarBorradorApi,
    leerBorradorApi,
    type EntradaAlta,
} from '../../lib/altas'

export interface UsoDelBorrador {
    /** Un aviso para la persona: guardado, choque, o contrato viejo. */
    aviso: string | null
    limpiarAviso: () => void
    guardando: boolean
    /** Guarda tal cual esté, sin validar. */
    guardar: (entrada: EntradaAlta, nombre: string) => Promise<void>
}

export function usarBorrador(
    idBorrador: number | null,
    alAbrir: (datos: EntradaAlta) => void,
    alCrear: (id: number) => void,
): UsoDelBorrador {
    /** La versión que se leyó al abrirlo. Es la llave del candado de FR-028. */
    const [version, setVersion] = useState<number | null>(null)
    const [aviso, setAviso] = useState<string | null>(null)
    const [guardando, setGuardando] = useState(false)
    const [roto, setRoto] = useState(false)

    useEffect(() => {
        if (idBorrador === null) return
        let vigente = true
        void (async () => {
            try {
                const b = await leerBorradorApi(idBorrador)
                if (!vigente) return
                alAbrir(b.datos)
                setVersion(b.version)
                // FR-027 · se avisa AL ABRIRLO, no al emitirlo. Esperar al final
                // sería dejar que alguien rellenara el formulario entero antes
                // de enterarse de que partía de algo caducado.
                if (b.estado.contratoDistinto) {
                    setAviso(
                        `Este borrador se empezó contra la versión ${b.versionContrato} del contrato de la ficha, y ya no es la vigente. Revísalo: puede que algo que entonces valía ahora no valga.`,
                    )
                }
            } catch {
                if (!vigente) return
                setAviso('No se pudo abrir ese borrador. Puede que alguien lo haya descartado.')
                // Y se marca como ROTO, para que guardar no cree uno nuevo: se
                // estaba editando el 7, y acabar con un 8 duplicado además de no
                // haber tocado el 7 es peor que no poder guardar.
                setRoto(true)
            }
        })()
        return () => {
            vigente = false
        }
        // Solo `idBorrador`, y a propósito: `alAbrir` y `alCrear` los recrea el
        // componente en cada render, así que ponerlos aquí volvería a cargar el
        // borrador sin parar y pisaría lo que se estuviera escribiendo.
    }, [idBorrador])

    async function guardar(entrada: EntradaAlta, nombre: string): Promise<void> {
        if (roto) {
            setAviso('No se pudo abrir este borrador, así que tampoco se puede guardar encima. Vuelve a la lista.')
            return
        }
        setGuardando(true)
        setAviso(null)
        try {
            if (idBorrador !== null && version !== null) {
                const r = await guardarBorradorApi(idBorrador, entrada, version, nombre)
                setVersion(r.version)
            } else {
                const r = await crearBorradorApi(nombre, entrada)
                setVersion(r.version)
                alCrear(r.id)
            }
            setAviso('Guardado. Puedes cerrar y seguir cuando quieras.')
        } catch (err) {
            setAviso(explicarFallo(err))
        } finally {
            setGuardando(false)
        }
    }

    return { aviso, limpiarAviso: () => setAviso(null), guardando, guardar }
}

/** El 409 trae dentro quién y cuándo. Sin eso, «conflicto» no dice nada. */
function explicarFallo(err: unknown): string {
    if (!(err instanceof ErrorApi) || err.status !== 409) {
        return 'No se pudo guardar el borrador.'
    }
    const c = err.cuerpo as { actualizadoPor?: string | null; actualizadoEn?: string } | undefined
    const quien = c?.actualizadoPor ?? 'otra persona'
    const cuando = c?.actualizadoEn
        ? new Date(c.actualizadoEn).toLocaleString('es-ES')
        : 'hace un momento'
    return `No se ha guardado: ${quien} lo modificó ${cuando}. Lo tuyo sigue en pantalla — ábrelo en otra pestaña para comparar antes de pisarlo.`
}
